import { useCallback } from "react";
import { METRICS_EVENTS } from "@cede/types";
import {
  ExchangeType,
  CampaignStatusType,
  SortField,
  ParticipationFilterType,
} from "@/components/features/campaigns/types";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";

interface UseCampaignsTableHandlersProps {
  selectedExchanges: ExchangeType[];
  selectedStatuses: CampaignStatusType[];
  selectedRules: string[];
  sortState: { field: SortField; direction: "asc" | "desc" };
  setSelectedExchanges: (exchanges: ExchangeType[]) => void;
  setSelectedStatuses: (statuses: CampaignStatusType[]) => void;
  setSelectedRules: (rules: string[]) => void;
  setParticipationFilter: (filter: ParticipationFilterType | null) => void;
  toggleSortField: (field: SortField) => void;
}

export const useCampaignsTableHandlers = ({
  selectedExchanges,
  selectedStatuses,
  selectedRules,
  sortState,
  setSelectedExchanges,
  setSelectedStatuses,
  setSelectedRules,
  setParticipationFilter,
  toggleSortField,
}: UseCampaignsTableHandlersProps) => {
  const { trackEvent } = useMetricsEvents();

  const handleExchangeFilterChange = useCallback(
    (exchangeId: string, checked: boolean) => {
      const currentExchanges = Array.isArray(selectedExchanges) ? selectedExchanges : [];
      const newSelection = checked
        ? [...currentExchanges, exchangeId as ExchangeType]
        : currentExchanges.filter((id) => id !== exchangeId);

      // Track when user filters campaigns by exchange
      trackEvent(METRICS_EVENTS.CAMPAIGNS_MENU_EXCHANGE_SELECTED, {
        exchange: newSelection,
      });
      setSelectedExchanges(newSelection);
    },
    [selectedExchanges, setSelectedExchanges, trackEvent],
  );

  const handleStatusFilterChange = useCallback(
    (status: string, checked: boolean) => {
      const currentStatuses = Array.isArray(selectedStatuses) ? selectedStatuses : [];
      const newSelection = checked
        ? [...currentStatuses, status as CampaignStatusType]
        : currentStatuses.filter((s) => s !== status);

      // Track when user filters campaigns by status (live, ended, etc.)
      trackEvent(METRICS_EVENTS.CAMPAIGNS_MENU_STATUS_SELECTED, {
        status: newSelection,
      });
      setSelectedStatuses(newSelection);
    },
    [selectedStatuses, setSelectedStatuses, trackEvent],
  );

  const handleParticipationFilterChange = useCallback(
    (filterId: string, checked: boolean) => {
      if (checked) {
        const filter = filterId as ParticipationFilterType;
        // Track when user filters campaigns by participation (joined, not joined, etc.)
        trackEvent(METRICS_EVENTS.CAMPAIGNS_MENU_PARTICIPATION_FILTER_SELECTED, {
          filter,
        });
        setParticipationFilter(filter);
      } else {
        // Track when user clears the participation filter
        trackEvent(METRICS_EVENTS.CAMPAIGNS_MENU_PARTICIPATION_FILTER_CLEARED, {});
        setParticipationFilter(null);
      }
    },
    [setParticipationFilter, trackEvent],
  );

  const handleParticipationRadioChange = useCallback(
    (value: string) => {
      if (value === "all") {
        // Track when user clears the participation filter (radio button "all" selected)
        trackEvent(METRICS_EVENTS.CAMPAIGNS_MENU_PARTICIPATION_FILTER_CLEARED, {});
        setParticipationFilter(null);
      } else {
        const filter = value as ParticipationFilterType;
        // Track when user filters campaigns by participation via radio button
        trackEvent(METRICS_EVENTS.CAMPAIGNS_MENU_PARTICIPATION_FILTER_SELECTED, {
          filter,
        });
        setParticipationFilter(filter);
      }
    },
    [setParticipationFilter, trackEvent],
  );

  const handleRuleFilterChange = useCallback(
    (displayName: string, checked: boolean) => {
      const currentRules = Array.isArray(selectedRules) ? selectedRules : [];
      const newSelection = checked ? [...currentRules, displayName] : currentRules.filter((r) => r !== displayName);

      // Track when user filters campaigns by rule type
      trackEvent(METRICS_EVENTS.CAMPAIGNS_MENU_RULE_SELECTED, {
        rules: newSelection,
      });
      setSelectedRules(newSelection);
    },
    [selectedRules, setSelectedRules, trackEvent],
  );

  const handleSortClick = useCallback(
    (field: SortField) => {
      // Track when user changes campaign sorting (by field and direction)
      trackEvent(METRICS_EVENTS.CAMPAIGNS_MENU_SORTING_SELECTED, {
        sorting: `${field}:${sortState.field === field && sortState.direction === "desc" ? "asc" : "desc"}`,
      });
      toggleSortField(field);
    },
    [sortState, toggleSortField, trackEvent],
  );

  return {
    handleExchangeFilterChange,
    handleStatusFilterChange,
    handleParticipationFilterChange,
    handleParticipationRadioChange,
    handleRuleFilterChange,
    handleSortClick,
  };
};
